// ============================== SubsectionHeader 模块开始 ==============================
// 该组件用于在"邀请"、"通知"等二级页面展示统一的顶部栏
// 技术栈：React 18 + TypeScript（strict 模式）+ Tailwind CSS + shadcn/ui
// 依赖：@/components/ui/button（shadcn 封装）、lucide-react（SVG 图标）、@/lib/utils（cn 工具函数）
// -----------------------------------------------------------------------------
// Props 说明：
// title     - 顶部栏标题文本，必传
// backPath  - 返回按钮跳转地址，可选，默认为"/"
// className - 允许外部覆盖/扩展样式，可选
// -----------------------------------------------------------------------------
// 使用示例（Rails 视图中嵌入）：
// <div
//   data-react-component="SubsectionHeader"
//   data-props="<%= { title: '通知中心', backPath: main_app.root_path }.to_json %>"
//   data-turbo="false"
// ></div>
// -----------------------------------------------------------------------------
// Author: AI Assistant
// ============================== SubsectionHeader 模块开始 ==============================

import { useTranslation } from "react-i18next";
import React from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import * as Icons from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ------------------------------ Props 类型定义 ------------------------------
export interface SubsectionHeaderProps {
  /** 主标题 */
  title: string;
  /** 副标题 / 描述，可选，支持字符串或 ReactNode */
  description?: React.ReactNode;
  /** Lucide 图标名称（如 "Bell"），可选 */
  icon?: string;
  /** 返回按钮跳转地址，若为空则不渲染返回按钮 */
  backPath?: string;
  /** 返回按钮样式变体（继承自 Button） */
  backButtonVariant?:
    | "default"
    | "secondary"
    | "outline"
    | "ghost"
    | "link"
    | "destructive";
  /** 返回按钮尺寸 */
  backButtonSize?: "default" | "sm" | "lg" | "icon";
  /** 布局变体：compact=粘性顶栏 | hero=块级大标题（默认） */
  variant?: "compact" | "hero";
  /** 允许外部传入额外 className */
  className?: string;
  /** 右侧自定义 Action 区域（仅 compact 模式生效） */
  actions?: React.ReactNode;
  /** 主操作按钮 */
  primaryAction?: {
    text: string;
    href: string;
    variant?:
      | "default"
      | "secondary"
      | "outline"
      | "ghost"
      | "link"
      | "destructive";
    size?: "default" | "sm" | "lg" | "icon";
    external?: boolean;
    /** Lucide 图标名称，可选 */
    icon?: string;
    /** 点击事件处理 */
    onClick?: (e: React.MouseEvent) => void;
    /** 额外 data-* 属性，将透传到 <a> */
    dataAttrs?: Record<string, any>;
  };
}
// -----------------------------------------------------------------------------

// 自定义 Spray Icon（与移动端入口保持一致）
const SprayIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24 24L34 14C34 8.47715 29.5228 4 24 4C18.4772 4 14 8.47715 14 14L24 24Z"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14 34L24 24L14 14C8.47715 14 4 18.4772 4 24C4 29.5228 8.47715 34 14 34Z"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M34 34C39.5228 34 44 29.5228 44 24C44 18.4772 39.5228 14 34 14L24 24L34 34Z"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24 44C29.5228 44 34 39.5228 34 34L24 24L14 34C14 39.5228 18.4772 44 24 44Z"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SubsectionHeader: React.FC<SubsectionHeaderProps> = ({
  title,
  description,
  icon,
  backPath,
  backButtonVariant = "secondary",
  backButtonSize = "sm",
  variant = "hero",
  className,
  actions,
  primaryAction,
}) => {
  const { t } = useTranslation("common");
  // 根据传入的 icon 名称动态获取 Lucide 组件，支持自定义 SprayIcon
  let IconComponent: React.ComponentType<{ className?: string }> | null = null;
  if (icon === "Spray") {
    //
    IconComponent = SprayIcon;
  } else if (icon && (Icons as any)[icon]) {
    IconComponent = (Icons as any)[icon] as LucideIcon;
  }

  // 处理主操作按钮图标
  let ActionIcon: React.ComponentType<{ className?: string }> | null = null;
  if (primaryAction?.icon && (Icons as any)[primaryAction.icon]) {
    ActionIcon = (Icons as any)[primaryAction.icon] as LucideIcon;
  }

  // ------------------------------ 返回按钮渲染逻辑 ------------------------------
  const isJsHistoryBack = backPath?.startsWith("javascript:");
  const backButtonElement = backPath ? (
    isJsHistoryBack ? (
      <Button
        variant={backButtonVariant}
        size={backButtonSize}
        className={variant === "compact" ? "shrink-0" : undefined}
        onClick={() => window.history.back()}
        aria-label="返回上一页"
      >
        {variant === "hero" ? (
          <>
            <ChevronLeft className="mr-1.5 size-4" /> {t("app.back")}
          </>
        ) : (
          <ChevronLeft className="size-5" />
        )}
      </Button>
    ) : (
      <Button
        variant={backButtonVariant}
        size={backButtonSize}
        className={variant === "compact" ? "shrink-0" : undefined}
        asChild
      >
        <a href={backPath} aria-label="返回上一页">
          {variant === "hero" ? (
            <>
              <ChevronLeft className="mr-1.5 size-4" /> {t("app.back")}
            </>
          ) : (
            <ChevronLeft className="size-5" />
          )}
        </a>
      </Button>
    )
  ) : null;

  if (variant === "compact") {
    return (
      <header
        className={cn(
          "sticky top-0 z-40 flex h-14 w-full items-center border-b bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/60",
          className,
        )}
        data-slot="subsection-header"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center px-4">
          {/* 左侧：返回按钮 + 标题 */}
          <div className="flex grow items-center gap-2">
            {backButtonElement}

            <h1 className="text-base font-semibold leading-none tracking-tight">
              {title}
            </h1>
          </div>

          {/* 右侧操作区域 */}
          {actions ||
            (primaryAction && (
              <Button
                variant={primaryAction.variant || "default"}
                size={primaryAction.size || "default"}
                asChild
              >
                <a
                  href={primaryAction.href}
                  onClick={primaryAction.onClick}
                  target={primaryAction.external ? "_blank" : undefined}
                  className="inline-flex items-center"
                  {...(primaryAction.dataAttrs as any)}
                >
                  {ActionIcon && <ActionIcon className="mr-1.5 size-4" />}
                  {primaryAction.text}
                </a>
              </Button>
            ))}
        </div>
      </header>
    );
  }

  // hero 模式
  return (
    <header
      className={cn("relative isolate overflow-hidden py-4 sm:py-5", className)}
      data-slot="subsection-header-hero"
    >
      <div className="mx-auto flex max-w-4xl flex-col items-center px-4 text-center">
        {/* 图标 */}
        {IconComponent && (
          <div className="mb-3 inline-flex items-center justify-center rounded-xl bg-muted p-2 text-foreground shadow-sm sm:mb-3">
            <IconComponent className="size-5 sm:size-6" />
          </div>
        )}

        {/* 主标题 */}
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {title}
        </h1>

        {/* 描述 */}
        {description && (
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:mt-2.5">
            {description}
          </p>
        )}

        {/* 操作按钮区域（hero 模式） */}
        {(primaryAction || backPath) && (
          <div className="mt-4 sm:mt-5 flex items-center gap-3">
            {/* 返回按钮 */}
            {backButtonElement}
            {/* 主操作按钮 */}
            {primaryAction && (
              <Button
                variant={primaryAction.variant || "default"}
                size={primaryAction.size || "default"}
                asChild
              >
                <a
                  href={primaryAction.href}
                  onClick={primaryAction.onClick}
                  target={primaryAction.external ? "_blank" : undefined}
                  className="inline-flex items-center"
                  {...(primaryAction.dataAttrs as any)}
                >
                  {ActionIcon && <ActionIcon className="mr-1.5 size-4" />}
                  {primaryAction.text}
                </a>
              </Button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default SubsectionHeader;

// ============================== SubsectionHeader 模块结束 ==============================
